import { formatIsoDateLocal } from '@/shared/utils/format'

/**
 * Domain helpers for the "Última actividad" admin column.
 * Pure functions — no React, no Firebase.
 */

/** How many milliseconds must elapse before another `lastActivityAt` write is
 * permitted. 15 minutes, matching the spec. */
export const ACTIVITY_DEBOUNCE_MS = 15 * 60 * 1000

/**
 * Formats a `lastActivityAt` Date into a human-readable relative or absolute
 * string for the admin Clubes table.
 *
 * Rules (relative to `now`):
 *   < 1 h  → "hace X min"
 *   < 24 h → "hace X h"
 *   < 30 d → "hace X días"
 *   ≥ 30 d → exact date "YYYY-MM-DD"
 *   null   → "Sin actividad"
 */
export function formatLastActivity(date: Date | null | undefined, now: Date): string {
  if (date == null) return 'Sin actividad'
  const diffMs = now.getTime() - date.getTime()
  if (diffMs < 0) return 'Sin actividad' // clock skew guard

  const diffMin = Math.floor(diffMs / 60_000)
  if (diffMin < 60) return `hace ${diffMin === 0 ? 1 : diffMin} min`

  const diffH = Math.floor(diffMs / 3_600_000)
  if (diffH < 24) return `hace ${diffH} h`

  const diffD = Math.floor(diffMs / 86_400_000)
  if (diffD < 30) return `hace ${diffD} ${diffD === 1 ? 'día' : 'días'}`

  return formatIsoDateLocal(date)
}

/**
 * Returns true when a new `lastActivityAt` write is allowed.
 * `lastWriteMs` is the epoch timestamp of the last write (from memory or
 * localStorage). Pass `null` if no write has ever been recorded.
 */
export function shouldWriteActivity(lastWriteMs: number | null, now: Date): boolean {
  if (lastWriteMs == null) return true
  return now.getTime() - lastWriteMs >= ACTIVITY_DEBOUNCE_MS
}
